// useCatalogueStatus.ts
import * as signalR from "@microsoft/signalr";
import { useEffect, useRef, useState } from "react";
import { API_URL } from "../_config/config.const";
import { isSessionSuperseded } from "../_helpers/session.helper";
import { disconnectAuctionUserHub, registerAuctionUserHubLifecycle, unregisterAuctionUserHubLifecycle } from "./auctionUserHubLifecycle";
import { accountService } from "../_services/accounts.service";

const API_NAME = "catalogueUser";

type ConnStatus = 'connected' | 'reconnecting' | 'disconnected';

export type UserPresence = 'online' | 'away' | 'reconnecting' | 'disconnected';

// Grace before showing full "Offline" after reconnect attempts fail.
const DISCONNECT_GRACE_MS = 1_000;

function readTabVisible(): boolean {
    if (typeof document === 'undefined') return true;
    return document.visibilityState === 'visible' && document.hasFocus();
}

export function useAuctionUser(onReconnected?: () => void) {
    const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
    const [status, setStatus] = useState<ConnStatus>('disconnected');
    const [stableConnected, setStableConnected] = useState(false);
    const [isTabVisible, setIsTabVisible] = useState(readTabVisible);
    const connRef = useRef<signalR.HubConnection | null>(null);
    const heartbeatRef = useRef<number | null>(null);
    const tabVisibleRef = useRef(isTabVisible);
    const onReconnectedRef = useRef(onReconnected);
    const statusRef = useRef<ConnStatus>('disconnected');
    const [currentUserId, setCurrentUserId] = useState(accountService.currentUserValue?.id);

    useEffect(() => {
        onReconnectedRef.current = onReconnected;
    }, [onReconnected]);

    useEffect(() => {
        tabVisibleRef.current = isTabVisible;
    }, [isTabVisible]);

    useEffect(() => {
        const currentUser = accountService.currentUserValue;

        const conn = new signalR.HubConnectionBuilder()
            .withUrl(`${API_URL}/hubs/${API_NAME}`, {
                accessTokenFactory: () => currentUser.token
            })
            .withAutomaticReconnect([0, 1000, 2000, 5000, 10000])
            .build();

        conn.serverTimeoutInMilliseconds = 10_000;
        conn.keepAliveIntervalInMilliseconds = 5_000;

        conn.on('OnlineUsersUpdated', (ids: string[]) => setOnlineUsers(ids));

        conn.on('SessionRevoked', (newSessionStamp: string) => {
            if (!isSessionSuperseded(accountService.currentUserValue?.token, newSessionStamp))
                return;
            accountService.logout('Your session ended because you signed in elsewhere.');
        });

        const setConnStatus = (next: ConnStatus) => {
            statusRef.current = next;
            setStatus(next);
        };

        const markConnected = (resync = true) => {
            const wasConnected = statusRef.current === 'connected';
            setConnStatus('connected');
            if (wasConnected) return;

            conn.invoke('UserConnected').catch(() => { });
            conn.invoke('AuctionHeartbeat', tabVisibleRef.current).catch(() => { });
            if (resync) onReconnectedRef.current?.();
        };

        const sendHeartbeat = () => {
            if (conn.state !== signalR.HubConnectionState.Connected) return;

            if (statusRef.current !== 'connected') {
                markConnected();
                return;
            }

            conn.invoke('AuctionHeartbeat', tabVisibleRef.current).catch(() => { });
        };

        conn.onreconnecting((_err) => setConnStatus('reconnecting'));
        conn.onreconnected((_id) => markConnected());
        conn.onclose((_err) => setConnStatus('disconnected'));

        connRef.current = conn;

        const disconnect = async () => {
            if (heartbeatRef.current) {
                clearInterval(heartbeatRef.current);
                heartbeatRef.current = null;
            }

            if (conn.state === signalR.HubConnectionState.Connected) {
                try {
                    await conn.invoke('UserLogout');
                } catch {
                }
            }

            try {
                await conn.stop();
            } catch {
            }

            setConnStatus('disconnected');
        };

        registerAuctionUserHubLifecycle(disconnect);

        heartbeatRef.current = window.setInterval(sendHeartbeat, 3000);

        (async () => {
            try {
                await conn.start();
                markConnected(false);
            } catch (err) {
                console.error('SignalR connect failed', err);
                setConnStatus('disconnected');
            }
        })();

        const onVisibilityChange = () => {
            const visible = readTabVisible();
            setIsTabVisible(visible);
            tabVisibleRef.current = visible;
            sendHeartbeat();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('focus', onVisibilityChange);
        window.addEventListener('blur', onVisibilityChange);

        const onOffline = () => setConnStatus('reconnecting');
        const onOnline = () => {
            if (conn.state === signalR.HubConnectionState.Connected) {
                markConnected();
                return;
            }
            if (conn.state === signalR.HubConnectionState.Reconnecting) return;

            conn.start().then(() => markConnected()).catch(() => setConnStatus('disconnected'));
        };
        window.addEventListener('offline', onOffline);
        window.addEventListener('online', onOnline);

        return () => {
            unregisterAuctionUserHubLifecycle(disconnect);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('focus', onVisibilityChange);
            window.removeEventListener('blur', onVisibilityChange);
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('online', onOnline);
            void disconnect();
        };
    }, [currentUserId]);

    const connected = status === 'connected';

    useEffect(() => {
        if (connected) {
            setStableConnected(true);
            return;
        }
        const t = window.setTimeout(() => setStableConnected(false), DISCONNECT_GRACE_MS);
        return () => clearTimeout(t);
    }, [connected]);

    const presence: UserPresence =
        status === 'reconnecting' ? 'reconnecting'
            : status === 'connected' ? (!isTabVisible ? 'away' : 'online')
                : !stableConnected ? 'disconnected'
                    : 'reconnecting';

    return { onlineUsers, connected: stableConnected, status, presence, isTabVisible, socketLive: status === 'connected' };
}
